import { observable, action, makeObservable } from 'mobx'
import { types as t } from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import fetch from '../../utils/fetch'
import errorToast from '../../utils/error-toast'

export const callCognito = (actionName, method: 'GET'|'PUT'|'POST', body?: Record<string, any>) => {
  return fetch(`/api/${actionName}`, {
    method: method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })
    .then(res => res.json())
    .catch(err => console.log(err))
}

const publicPost = async (path: string, params: Record<string, any>) => {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    body: JSON.stringify(params)
  })
  const result = await response.json()
  if (response.status !== 200) {
    throw new Error((result && result.message) || result)
  }
  return result
}

interface UserProviderConfig {
  id: string
}

class UserProvider {
  data = {}
  changedData = {}
  isLoading = false
  config: UserProviderConfig
  constructor (config: UserProviderConfig) {
    makeObservable(this, {
      data: observable,
      isLoading: observable,
      setIsLoading: action,
      setIsLoadingOff: action,
      onValueChange: action
    })
    this.config = config
  }

  setIsLoading = () => {
    this.isLoading = true
  }

  setIsLoadingOff = () => {
    this.isLoading = false
  }

  onValueChange = (fieldpath, v) => {
    this.data[fieldpath] = v
    this.changedData[fieldpath] = v
  }

  signUp = async ({ username, password, ...rest }: Record<string, string>) => {
    this.setIsLoading()
    try {
      return (await publicPost('/api/user/public/signUp', { username, password, ...rest }))
    } finally {
      this.setIsLoadingOff()
    }
  }

  confirmSignUp = async ({ username, confirmationCode }: Record<string, string>) => {
    this.setIsLoading()
    try {
      return (await publicPost('/api/user/public/confirmSignUp', { username, confirmationCode }))
    } finally {
      this.setIsLoadingOff()
    }
  }

  forgotPassword = async ({ username }: Record<string, string>) => {
    this.setIsLoading()
    try {
      return (await publicPost('/api/user/public/forgotPassword', { username }))
    } finally {
      this.setIsLoadingOff()
    }
  }

  confirmForgotPassword = async ({ username, confirmationCode, password }: Record<string, string>) => {
    this.setIsLoading()
    try {
      return (await publicPost('/api/user/public/confirmForgotPassword', { username, confirmationCode, password }))
    } finally {
      this.setIsLoadingOff()
    }
  }

  login = async ({ username, password }: Record<string, string>) => {
    try {
      this.setIsLoading()
      const { sessionId } = await publicPost('/auth/login', { username, password })
      if (sessionId) window.location.href = `/callback?session=${encodeURIComponent(sessionId)}`
    } finally {
      this.setIsLoadingOff()
    }
  }

  logout = () => {
    window.location.href = '/api/user/logout'
  }

  changePassword = (user: { newPassword: string, oldPassword: string}) => {
    callCognito('user/changePassword', 'POST', {
      newPassword: user.newPassword,
      oldPassword: user.oldPassword
    })
      .catch(er => errorToast('Error changing password: ', er))
      .then(this.setIsLoadingOff)
  }

  updateUser = (_, cb: () => void) => {
    if (!Object.keys(this.changedData).length) {
      return
    }
    this.setIsLoading()
    const dataToSend = {
      attributes: Object.keys(this.changedData).reduce((acc, key) => {
        acc.push({
          Name: key,
          Value: this.data[key]
        })
        return acc
      }, [])
    }

    callCognito('user/edit', 'POST', dataToSend)
      .catch(er => errorToast('Error updating user data: ', er))
      .then(() => {
        this.setIsLoadingOff()
        typeof cb === 'function' && cb()
      })
  }

  query = context => {
    return new Promise((resolve, reject) => {
      if (this.data['sub']) {
        this.setIsLoadingOff()
        return resolve(this.data)
      }
      this.setIsLoading()
      callCognito('user', 'GET')
        .then(
          action(result => {
            this.data = result
            this.changedData = {}
            resolve(this.data)
          })
        )
        .catch(er => errorToast('Error querying user data: ', er))
        .then(this.setIsLoadingOff)
    })
  }
}

const fieldSet = [{ fieldPath: 'Username', label: 'Username', type: 'STRING' }]
export default t
  .model('UserConfig', {
    type: t.optional(t.string, 'User')
  })
  .views(self => ({
    get icon () {
      return 'person'
    },
    get provider () {
      return UserProvider
    },
    fieldPath (node) {
      return node.meta && node.meta.fieldPath
    },
    getFields () {
      return fieldSet
    },
    get fieldSet () {
      return fieldSet
    },
    async fetchChildNodes () {
      return self && (self as any).labels().childNodes[1].childNodes
    },
    labels () {
      const fields = fieldSet.map(attr => {
        return {
          key: attr.fieldPath + '',
          label: attr.label + '',
          fieldType: attr.type + '',
          type: attr.type + '',
          asynch: attr.type === 'REFERENCE',
          childNodes: attr.type === 'REFERENCE' && [],
          meta: {
            fieldPath: attr.fieldPath,
            label: attr.label,
            type: attr.type
          }
        }
      })

      const tempProvider = new (self as any).provider()
      const functions = Object.keys(tempProvider).filter(
        k => typeof tempProvider[k] === 'function'
      )

      const childNodes = functions.map(f => ({
        key: f,
        label: f,
        meta: { fieldPath: f },
        secondaryLabel: 'FUNCTION'
      }))
      childNodes.push({
        key: 'onValueChange',
        label: 'onValueChange',
        secondaryLabel: 'FUNCTION'
      } as any)

      return {
        key: (self as any).id,
        label: (self as any).name,
        childNodes: [
          {
            key: 'methods',
            label: 'Methods',
            childNodes: childNodes
          },
          {
            key: 'data',
            label: 'Data',
            asynch: true,
            childNodes: fields
          }
        ]
      }
    }
  }))
  .actions(crudActions)
